import { ErrorHandler, Injectable } from '@angular/core'

import { KayttajaService } from '../kayttaja.service'

import { Kayttaja, KayttajanTiedot } from '@lemon/core-firebase/model/kayttaja'
import {
  AsiakkaanTuote,
  EI_LASKUNUMEROA_NUMERO,
  EmailLahetysStatus,
  Lasku,
  Laskuasetukset,
  LaskuBase,
  LaskunAsiakas,
  LaskunLahetystapa,
  LaskunLiitetiedosto,
  LaskunListaustietorivi,
  LaskunSahkoinenOsoite,
  LaskunumeroTyyppi,
  LaskuReskontra,
  LaskuToimintaloki,
  LaskuToimittaja,
  PeppolNumberResponseEntity,
  PeppolValidaatioTulos,
  TypeaheadAsiakas,
  TypeaheadAsiakasDokumentti,
  TypeaheadToimittaja,
  TypeaheadToimittajaDokumentti,
  TypeaheadTuote,
  TypeaheadTuoteDokumentti,
  UUDEN_LASKUN_AVAIN,
} from '@lemon/core-firebase/model/lasku'
import { LaskuLahetyksenToistoasetukset, ToistuvaLasku } from '@lemon/core-firebase/model/lasku-toistuva'
import { LaskuLahetyksenToistoService } from '@lemon/core-firebase/service/lasku/lasku-lahetyksen-toisto.service'

import { LadattavaLiitetiedosto } from '../../../laskut/lasku-rivit.component'

import { LaskuSharedService, ReskontraService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { FirestoreProvider, FirestoreWriteBatch, JuurilaskuJaKasiteltava, LaskuTallennusService } from '@lemon/core-firebase/service/lasku/lasku-tallennus.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { LaskuIndeksoija } from '@lemon/core-firebase/service/lasku/lasku.indeksoija'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { TranslationService } from '@lemon/core-firebase/service/translation.service'
import { TuettuKieli } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { StringService } from '@lemon/core/service/string.service'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'

import { Timestamp } from '@lemon/core/model/common'
import { firstValueFrom, Observable, of as observableOf, ReplaySubject } from 'rxjs'
import { map, mergeMap, switchMap } from 'rxjs/operators'
import { TimestampService } from '../../../_jaettu-angular/service/timestamp-service'

import { PeppolNameObject, PeppolNumberRequest, PeppolNumberResponse } from '@lemon/core-firebase/model/lasku'
import { WebLaskuTallennaPyynto, WebLaskuTallennaVastaus } from '@lemon/core-firebase/model/web-lasku-api'
import { FirebaseLemonaid } from '../firebase-lemonaid.service'

@Injectable()
export class LaskuService {
  private laskuTallennusService: LaskuTallennusService

  private asetuksetSubject = new ReplaySubject<Laskuasetukset>(1)
  asetuksetObservable = this.asetuksetSubject.asObservable()

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public LEMONTREE_ASIAKAS_ID = 9
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public LEMONTREE_ASIAKAS_AVAIN = 'jzATU474P4BRCkEANZ1H'

  constructor(
    private errorHandler: ErrorHandler,
    private kayttajaService: KayttajaService,
    private lemonaidFirebase: FirebaseLemonaid,
    private shared: LaskuSharedService,
    private laskuIndeksoija: LaskuIndeksoija,
    private laskuKopioija: LaskuKopioija,
    private currencyService: CurrencyService,
    private laskuUriService: LaskuUriService,
    private dateService: DateService,
    private translationService: TranslationService,
    private timestampService: TimestampService,
    private reskontraService: ReskontraService,
    private viitenumeroService: ViitenumeroService,
    private stringService: StringService,
    private toistoService: LaskuLahetyksenToistoService,
    private _kopioijaPalvelu: KopioijaPalvelu,
  ) {
    const firestoreProvider: FirestoreProvider = {
      annaDeleteArvo: () => {
        return lemonaidFirebase.firestoreDeleteMarker()
      },
      annaUusiAvain: () => {
        return this.lemonaidFirebase.firestoreCreateId()
      },
      // THIS IS REALLY COUNTERINTUITIVE, BUT THE DOC TYPE CONTAINS NO DATA
      // AND THESE DAYS OUR FRONT END FIREBASE INTEGRATION LIBRARY EXPECTS THE URI,
      // NOT THE DOC TO BE PRESENT!
      annaDoc: (uri: string) => {
        return uri
      },
      annaUusiBatch: () => {
        return this.lemonaidFirebase.firestoreBatch()
      },
    }

    this.laskuTallennusService = new LaskuTallennusService(
      this.shared,
      this.laskuKopioija,
      this.laskuIndeksoija,
      this.currencyService,
      this.dateService,
      this.laskuUriService,
      this.translationService,
      this.timestampService,
      this.reskontraService,
      firestoreProvider,
      this.viitenumeroService,
      this.stringService,
    )

    this.kayttajaService.kayttajanTiedotObservable
      .pipe(
        switchMap(kayttajanTiedot => {
          if (kayttajanTiedot) {
            const asetuksetUri = this.laskuUriService.getLaskuasetuksetUri(kayttajanTiedot.asiakasAvain, kayttajanTiedot.asiakasAvain)
            return this.lemonaidFirebase.firestoreDoc<Laskuasetukset>(asetuksetUri).listen()
          }
          return observableOf<Laskuasetukset>(null)
        }),
      )
      .subscribe(asetukset => {
        this.asetuksetSubject.next(asetukset ?? null)
      })
  }

  annaAsetukset(kayttajanTiedot: KayttajanTiedot): Promise<Laskuasetukset | null> {
    if (kayttajanTiedot && kayttajanTiedot.asiakasAvain) {
      const asetuksetUri = this.laskuUriService.getLaskuasetuksetUri(kayttajanTiedot.asiakasAvain, kayttajanTiedot.asiakasAvain)
      return this.lemonaidFirebase.firestoreDoc<Laskuasetukset>(asetuksetUri).get()
    }
    return Promise.resolve(null)
  }

  annaAsetuksetKayttajalle(kayttaja: Kayttaja): Promise<Laskuasetukset | null> {
    if (kayttaja && kayttaja.asiakasAvain) {
      const asetuksetUri = this.laskuUriService.getLaskuasetuksetUri(kayttaja.asiakasAvain, kayttaja.asiakasAvain)
      return this.lemonaidFirebase.firestoreDoc<Laskuasetukset>(asetuksetUri).get()
    }
    return Promise.resolve(null)
  }

  async poistaAsiakkaat(asiakkaat: LaskunAsiakas[]) {
    if (asiakkaat) {
      const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
      const batch = this.lemonaidFirebase.firestoreBatch()

      for (const asiakas of asiakkaat) {
        asiakas.date = this.timestampService.now()
        asiakas.poistettu = true
        this.laskuTallennusService.lisaaAsiakasBatchiin(kayttajanTiedot.asiakasId, kayttajanTiedot.asiakasAvain, asiakas, batch)
      }

      return batch.commit().catch(error => {
        this.errorHandler.handleError(error)
      })
    }
  }

  async updateAsiakkaat(asiakkaat: LaskunAsiakas[]) {
    if (asiakkaat) {
      const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
      const batch = this.lemonaidFirebase.firestoreBatch()

      for (const asiakas of asiakkaat) {
        asiakas.date = this.timestampService.now()
        this.laskuTallennusService.lisaaAsiakasBatchiin(kayttajanTiedot.asiakasId, kayttajanTiedot.asiakasAvain, asiakas, batch)
      }

      return batch.commit().catch(error => {
        this.errorHandler.handleError(error)
      })
    }
  }

  public getTypeaheadAsiakkaat(kayttajanTiedot: KayttajanTiedot): Observable<TypeaheadAsiakas[]> {
    if (!kayttajanTiedot) {
      return observableOf([])
    }
    const typeaheadUri = this.laskuUriService.getTypeaheadAsiakasDokumentinUriNew(kayttajanTiedot.asiakasAvain)
    return this.lemonaidFirebase
      .firestoreDoc<TypeaheadAsiakasDokumentti>(typeaheadUri)
      .listen()
      .pipe(
        map(dokumentti => {
          const asiakkaat = dokumentti || {}
          return Object.keys(asiakkaat).map((key, index) => {
            const value = asiakkaat[key]
            const asiakas: TypeaheadAsiakas = {
              avain: key,
              nimi: value.nimi,
              ytunnus: value.ytunnus,
            }
            return asiakas
          })
        }),
      )
  }

  getAsiakasObservable(id: string, asiakasAvain?: string): Observable<LaskunAsiakas | null> {
    if (asiakasAvain) {
      return this.getAsiakasInternal(id, asiakasAvain)
    } else {
      return this.kayttajaService.kayttajanTiedotObservable.pipe(
        mergeMap(kayttajanTiedot => {
          if (kayttajanTiedot) {
            return this.getAsiakasInternal(id, kayttajanTiedot.asiakasAvain)
          }
          return observableOf(null)
        }),
      )
    }
  }

  private getAsiakasInternal(id: string, asiakasAvain: string): Observable<LaskunAsiakas | null> {
    return this.lemonaidFirebase
      .firestoreDoc<LaskunAsiakas>(this.laskuUriService.getLaskuAsiakasUri(asiakasAvain, id))
      .listenSnap()
      .pipe(
        map(snapshot => {
          const asiakas = snapshot.data() as LaskunAsiakas
          if (asiakas) {
            asiakas.avain = snapshot.id
            return asiakas
          }
          return null
        }),
      )
  }

  async getTuote(id: string): Promise<AsiakkaanTuote> {
    return firstValueFrom(this.getTuoteObservable(id))
  }

  getTuoteObservable(id: string, asiakasAvain?: string): Observable<AsiakkaanTuote> {
    if (asiakasAvain) {
      return this.getTuoteObservableInternal(id, asiakasAvain)
    } else {
      return this.kayttajaService.kayttajanTiedotObservable.pipe(
        mergeMap(asiakkaanTiedot => {
          return this.getTuoteObservableInternal(id, asiakkaanTiedot.asiakasAvain)
        }),
      )
    }
  }

  private getTuoteObservableInternal(id: string, asiakasAvain: string): Observable<AsiakkaanTuote> {
    return this.lemonaidFirebase
      .firestoreDoc<AsiakkaanTuote>(this.laskuUriService.getLaskuTuoteUri(asiakasAvain, id))
      .listenSnap()
      .pipe(
        map(snapshot => {
          if (snapshot.exists()) {
            const asiakkaanTuote = snapshot.data() as AsiakkaanTuote
            asiakkaanTuote.$key = snapshot.id
            return asiakkaanTuote
          }
          return null
        }),
      )
  }

  async poistaTuotteet(tuotteet: AsiakkaanTuote[]) {
    if (tuotteet) {
      const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
      const batch = this.lemonaidFirebase.firestoreBatch()

      for (const tuote of tuotteet) {
        tuote.poistettu = true
        this.laskuTallennusService.lisaaTuoteBatchiin(kayttajanTiedot.asiakasId, kayttajanTiedot.asiakasAvain, tuote, batch)
      }

      return batch.commit().catch(error => {
        this.errorHandler.handleError(error)
      })
    }
  }

  async updateTuotteet(tuotteet: AsiakkaanTuote[]) {
    if (tuotteet) {
      const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
      const batch = this.lemonaidFirebase.firestoreBatch()

      for (const tuote of tuotteet) {
        this.laskuTallennusService.lisaaTuoteBatchiin(kayttajanTiedot.asiakasId, kayttajanTiedot.asiakasAvain, tuote, batch)
      }

      return batch.commit().catch(error => {
        this.errorHandler.handleError(error)
      })
    }
  }

  public getTypeaheadTuotteet(kayttajanTiedot: KayttajanTiedot): Observable<TypeaheadTuote[]> {
    const typeaheadUri = this.laskuUriService.getTypeaheadTuoteDokumentinUriNew(kayttajanTiedot.asiakasAvain)
    return this.lemonaidFirebase
      .firestoreDoc<TypeaheadTuoteDokumentti>(typeaheadUri)
      .listen()
      .pipe(
        map(dokumentti => {
          const asiakkaat = dokumentti || {}
          return Object.keys(asiakkaat).map((key, index) => {
            const value = asiakkaat[key]
            const asiakas: TypeaheadTuote = {
              avain: key,
              nimi: value.nimi,
            }
            if (value.valuutta) {
              asiakas.valuutta = value.valuutta
            }
            if (value.hinta !== null && value.hinta !== undefined) {
              asiakas.hinta = value.hinta
            }
            if (value.yksikko) {
              asiakas.yksikko = value.yksikko
            }
            return asiakas
          })
        }),
      )
  }

  async annaSeuraavaToimittajaNumero(asiakasAvain: string): Promise<number> {
    const uri = this.laskuUriService.getLaskuToimittajatCollectionUri(asiakasAvain)
    const results = await this.lemonaidFirebase.firestoreCollection<LaskuToimittaja>(uri).orderBy('numero', 'desc').limit(1).get()

    if (results.length === 1) {
      return results[0].numero + 1
    }
    return 1
  }

  async onkoToimittajaNumeroKaytossa(asiakasAvain: string, numero: number): Promise<boolean> {
    const uri = this.laskuUriService.getLaskuToimittajatCollectionUri(asiakasAvain)
    const snap = await this.lemonaidFirebase
      .firestoreCollection<LaskuToimittaja>(uri)
      .where('numero', '==', numero)
      // Huom! Poistetun toimittajan numeroa ei voi käyttää uudelleen
      .limit(1)
      .get()
    return snap.length ? true : false
  }

  async getToimittaja(id: string): Promise<LaskuToimittaja> {
    return firstValueFrom(this.getToimittajaObservable(id))
  }

  getNewToimittaja(): Observable<LaskuToimittaja> {
    return this.kayttajaService.kayttajanTiedotObservable.pipe(
      mergeMap(asiakkaanTiedot => this.annaSeuraavaToimittajaNumero(asiakkaanTiedot.asiakasAvain)),
      map(numero => {
        const toimittaja = this.laskuKopioija.annaUusiToimittaja()
        toimittaja.numero = numero
        return toimittaja
      }),
    )
  }

  getToimittajaObservable(id: string, asiakasAvain?: string): Observable<LaskuToimittaja> {
    if (asiakasAvain) {
      return this.getToimittajaObservableInternal(id, asiakasAvain)
    } else {
      return this.kayttajaService.kayttajanTiedotObservable.pipe(
        mergeMap(asiakkaanTiedot => {
          return this.getToimittajaObservableInternal(id, asiakkaanTiedot.asiakasAvain)
        }),
      )
    }
  }

  private getToimittajaObservableInternal(id: string, asiakasAvain: string): Observable<LaskuToimittaja> {
    return this.lemonaidFirebase
      .firestoreDoc<LaskuToimittaja>(this.laskuUriService.getLaskuToimittajaUri(asiakasAvain, id))
      .listenSnap()
      .pipe(
        map(snapshot => {
          if (snapshot.exists()) {
            const data = snapshot.data() as LaskuToimittaja
            return data
          }
          return null
        }),
      )
  }

  lisaaToimittajaBatchiin(asiakasId: string, asiakasAvain: string, uid: string, toimittaja: LaskuToimittaja, batch: FirestoreWriteBatch) {
    this.laskuTallennusService.lisaaToimittajaBatchiin(asiakasId, asiakasAvain, uid, toimittaja, batch)
  }

  async validoiToimittaja(
    alkuperainen: LaskuToimittaja,
    toimittaja: LaskuToimittaja,
  ): Promise<{
    numero: 'duplicate' | null
  }> {
    const kayttajanTiedot = await firstValueFrom(this.kayttajaService.kayttajanTiedotObservable)

    const errors = {
      numero: null,
    }
    const onkoUusi = !toimittaja.avain

    if (onkoUusi || alkuperainen?.numero !== toimittaja.numero) {
      const toimittajaNumeroKaytossa = await this.onkoToimittajaNumeroKaytossa(kayttajanTiedot.asiakasAvain, toimittaja.numero)
      if (toimittajaNumeroKaytossa) {
        errors.numero = 'duplicate'
      }
    }

    return errors
  }

  async poistaToimittajat(toimittajat: LaskuToimittaja[]) {
    if (toimittajat) {
      const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
      const batch = this.lemonaidFirebase.firestoreBatch()

      for (const toimittaja of toimittajat) {
        toimittaja.poistettu = true
        this.laskuTallennusService.lisaaToimittajaBatchiin(kayttajanTiedot.asiakasId, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, toimittaja, batch)
      }

      return batch.commit().catch(error => {
        this.errorHandler.handleError(error)
      })
    }
  }

  async updateToimittajat(toimittajat: LaskuToimittaja[]) {
    if (toimittajat) {
      const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
      const batch = this.lemonaidFirebase.firestoreBatch()

      for (const toimittaja of toimittajat) {
        this.laskuTallennusService.lisaaToimittajaBatchiin(kayttajanTiedot.asiakasId, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, toimittaja, batch)
      }

      return batch.commit().catch(error => {
        this.errorHandler.handleError(error)
      })
    }
  }

  public getTypeaheadToimittajat(kayttajanTiedot: KayttajanTiedot): Observable<TypeaheadToimittaja[]> {
    const typeaheadUri = this.laskuUriService.getTypeaheadToimittajaDokumentinUriNew(kayttajanTiedot.asiakasAvain)
    return this.lemonaidFirebase
      .firestoreDoc<TypeaheadToimittajaDokumentti>(typeaheadUri)
      .listen()
      .pipe(
        map(dokumentti => {
          const toimittajat = dokumentti || {}
          return Object.keys(toimittajat).map((key, index) => {
            const value = toimittajat[key]
            const toimittaja: TypeaheadToimittaja = {
              avain: key,
              numero: value.numero,
              nimi: value.nimi,
              ytunnus: value.ytunnus,
              poistettu: value.poistettu,
            }
            return toimittaja
          })
        }),
      )
  }

  async getLasku(id: string): Promise<Lasku> {
    return firstValueFrom(this.getLaskuObservable(id))
  }

  async getLaskuAsiakkaalle(asiakasAvain: string, id: string): Promise<Lasku | null> {
    return firstValueFrom(this.getLaskuObservableAsiakkaalle(asiakasAvain, id))
  }

  getLaskuObservableAsiakkaalle(asiakasAvain: string, id: string): Observable<Lasku | null> {
    const laskuUri = this.laskuUriService.getCustomerLaskuUri(asiakasAvain, id)
    return this.lemonaidFirebase
      .firestoreDoc<Lasku>(laskuUri)
      .listen()
      .pipe(
        map(lasku => {
          if (lasku) {
            delete lasku['haku']
          }
          return lasku
        }),
      )
  }

  getLaskuReskontraObservable(laskuAvain: string): Observable<LaskuReskontra[]> {
    return this.kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (kayttajanTiedot) {
          const laskuUri = this.laskuUriService.getCustomerLaskunReskontraCollectionUri(kayttajanTiedot.asiakasAvain, laskuAvain)
          return this.lemonaidFirebase.firestoreCollection<LaskuReskontra>(laskuUri).orderBy('pvm', 'asc').listen()
        }
        return observableOf<LaskuReskontra[]>(null as [])
      }),
    )
  }

  getLaskuToimintalokiObservable(laskuAvain: string): Observable<LaskuToimintaloki[]> {
    return this.kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (kayttajanTiedot) {
          const toimintalokiUri = this.laskuUriService.getCustomerLaskunToimintalokiCollectionUri(kayttajanTiedot.asiakasAvain, laskuAvain)
          return this.lemonaidFirebase.firestoreCollection<LaskuToimintaloki>(toimintalokiUri).orderBy('pvm', 'asc').orderBy('toiminto', 'asc').listen()
        }
        return observableOf<LaskuToimintaloki[]>([])
      }),
    )
  }

  getLaskuObservable(avain: string): Observable<Lasku | null> {
    return this.kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (kayttajanTiedot) {
          return this.getLaskuObservableAsiakkaalle(kayttajanTiedot.asiakasAvain, avain)
        }
        return observableOf<Lasku>(null)
      }),
    )
  }

  haeLaskunumeroArvio(lasku: LaskuBase): Promise<number> {
    if (lasku.nro && lasku.nro !== EI_LASKUNUMEROA_NUMERO) {
      return Promise.resolve(lasku.nro)
    }
    const numerotyyppi = lasku.nrotyyppi === LaskunumeroTyyppi.HYVITYS ? LaskunumeroTyyppi.TAVALLINEN : lasku.nrotyyppi
    return this.getLaskunumeroInternal(lasku.pvm, lasku.avain, numerotyyppi).then(number => {
      const year = lasku.pvm.toDate().getFullYear().toString().substr(-2)
      return Number(year + number)
    })
  }

  private async getLaskunumeroInternal(laskupvm: Timestamp, laskuid: string, tyyppi: LaskunumeroTyyppi): Promise<number> {
    const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
    const uri = '/laskut/nrot/' + kayttajanTiedot.asiakasId + '/' + tyyppi + '/' + laskupvm.toDate().getFullYear()
    return this.lemonaidFirebase.databaseGet(uri).then(snapshot => {
      const values = snapshot.val()
      if (values) {
        if (laskuid) {
          let index = 1
          for (const numero of values) {
            if (numero === laskuid) {
              return index
            }
            index++
          }
          return index
        } else {
          return values.length + 1
        }
      } else {
        return 1
      }
    })
  }

  updateLaskuasetukset(asetukset: Laskuasetukset, laskutusprofiiliAvain?: string): Promise<void> {
    if (asetukset) {
      const copied = this.laskuKopioija.copyAsetukset(asetukset)
      copied.date = this.timestampService.now()

      return this.kayttajaService.getKayttajanTiedot().then(kayttajanTiedot => {
        if (kayttajanTiedot) {
          const uri = this.laskuUriService.getLaskuasetuksetUri(kayttajanTiedot.asiakasAvain, laskutusprofiiliAvain || kayttajanTiedot.asiakasAvain)
          return this.lemonaidFirebase.firestoreSetData<Laskuasetukset>(uri, copied)
        } else {
          return Promise.reject('Asetuksien tallentamisessa ei löytynyt käyttäjää!')
        }
      })
    }
  }

  private valmisteleTallennustaVarten(kasiteltava: LaskuBase) {
    if (kasiteltava.liitteet) {
      const jaljella: LaskunLiitetiedosto[] = []
      for (const liite of kasiteltava.liitteet) {
        const ladattavaliite = liite as LadattavaLiitetiedosto
        if (!ladattavaliite.uploadData || (!ladattavaliite.uploadError && ladattavaliite.uploadDone)) {
          jaljella.push(liite)
        }
      }
      kasiteltava.liitteet = jaljella
    }
  }

  async lahetaLaskuUudelleen(juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    return this._tallennaLasku({ toiminto: 'uudelleen-laheta', juurilasku, kasiteltavaAvain: kasiteltava.avain }, kasiteltava)
  }

  async lahetaEmailUudelleen(juurilasku: Lasku, kasiteltava: LaskuBase, vastaanottaja: EmailLahetysStatus, vanhaEmail: string): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'uudelleen-email', juurilasku, kasiteltavaAvain: kasiteltava.avain, uudelleenlahetettavaVastaanottaja: vastaanottaja, vanhaEmail }, kasiteltava)
  }

  async lahetaSahkoinenUudelleen(juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'uudelleen-sahkoinen-lahetys', juurilasku, kasiteltavaAvain: kasiteltava.avain }, kasiteltava)
  }

  async peruutaLuottotappiomerkinta(juurilasku: Lasku): Promise<JuurilaskuJaKasiteltava> {
    const viimeisinTavallinen = this.shared.annaViimeisinTavallinenLasku(juurilasku)
    this.valmisteleTallennustaVarten(viimeisinTavallinen)
    return this._tallennaLasku({ toiminto: 'luottotappio-peruutus', juurilasku, kasiteltavaAvain: viimeisinTavallinen.avain }, viimeisinTavallinen)
  }

  async paivitaTilaJaIndeksoiUudelleen(juurilasku: Lasku): Promise<JuurilaskuJaKasiteltava> {
    const kasiteltava = this.shared.annaViimeisinEiLuonnosLasku(juurilasku)
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'tila-ja-indeksi', juurilasku })
  }

  async indeksoiUudelleen(juurilasku: Lasku): Promise<JuurilaskuJaKasiteltava> {
    return this._tallennaLasku({ toiminto: 'indeksi', juurilasku })
  }

  async laskeReskontraUudelleen(juurilasku: Lasku): Promise<JuurilaskuJaKasiteltava> {
    return this._tallennaLasku({ toiminto: 'reskontra-uudelleen', juurilasku })
  }

  async merkitseLuottotappioksi(juurilasku: Lasku): Promise<JuurilaskuJaKasiteltava> {
    const viimeisinTavallinen = this.shared.annaViimeisinTavallinenLasku(juurilasku)
    this.valmisteleTallennustaVarten(viimeisinTavallinen)
    return this._tallennaLasku({ toiminto: 'luottotappio', juurilasku, kasiteltavaAvain: viimeisinTavallinen.avain }, viimeisinTavallinen)
  }

  async tallennaKommentti(juurilasku: Lasku, kommentti: string): Promise<JuurilaskuJaKasiteltava> {
    const kasiteltava = this.shared.annaViimeisinTavallinenLasku(juurilasku) ?? this.shared.annaViimeisinKasiteltavaLasku(juurilasku)
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'kommentti', juurilasku, kasiteltavaAvain: kasiteltava.avain, kommentti }, kasiteltava)
  }

  async lisaaReskontraMerkinta(juurilasku: Lasku, suoritus: number): Promise<JuurilaskuJaKasiteltava> {
    const kasiteltava = this.shared.annaViimeisinTavallinenLasku(juurilasku)
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'reskontramerkinta', juurilasku, kasiteltavaAvain: kasiteltava.avain, suoritus }, kasiteltava)
  }

  async merkitseLaskuTaysinMaksetuksi(listaustieto: LaskunListaustietorivi): Promise<JuurilaskuJaKasiteltava> {
    const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
    const juurilasku = await this.getLaskuAsiakkaalle(kayttajanTiedot.asiakasAvain, listaustieto.juuriAvain)
    return this._tallennaLasku({ toiminto: 'taysin-maksetuksi', juurilasku })
  }

  async poistaLuonnos(juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'poista-luonnos', juurilasku, kasiteltavaAvain: kasiteltava.avain }, kasiteltava)
  }

  async tallennaLuonnos(juurilasku: Lasku, kasiteltava: LaskuBase, toistuvaLaskuAihio?: boolean, ensimmainenLahetyspvm?: number): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'luonnos', juurilasku, kasiteltavaAvain: kasiteltava.avain, toistuvaLaskuAihio, ensimmainenLahetyspvm }, kasiteltava)
  }

  paivitaTuotteidenJaAsiakkaanAvaimet(juurilasku: Lasku, lahetystapa: LaskunLahetystapa): void {
    this.laskuTallennusService.paivitaTuotteidenJaAsiakkaanAvaimet(juurilasku, lahetystapa)
  }

  async luoToistuvaLasku(juurilasku: Lasku, asetukset: LaskuLahetyksenToistoasetukset): Promise<void> {
    const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()

    const toistuvaLasku: ToistuvaLasku = {
      lasku: juurilasku,
      toisto: asetukset,
      seuraavaLahetysPvm: asetukset.ensimmainenLahetyspvm,
      lahetyksia: 0,
      poistettu: false,
      paivitetty: this.timestampService.now(),
      paivittaja: kayttajanTiedot.uid,
    }

    const toistuvaLaskuUri = this.laskuUriService.getToistuvanLaskunUri(kayttajanTiedot.asiakasAvain, juurilasku.avain)
    const batch = this.lemonaidFirebase.firestoreBatch()
    batch.set(toistuvaLaskuUri, toistuvaLasku)

    this.lisaaToistuvanLaskunHistoriaMerkinta(kayttajanTiedot, juurilasku.avain, toistuvaLasku, batch)

    await batch.commit()
  }

  private lisaaToistuvanLaskunHistoriaMerkinta(kayttajanTiedot: KayttajanTiedot, laskuAvain: string, toistuvaLasku: ToistuvaLasku, batch: FirestoreWriteBatch): void {
    const historiaAvain = this.lemonaidFirebase.firestoreCreateId()
    const historiaUri = this.laskuUriService.getToistuvanLaskunHistoriaAvaimellaUri(kayttajanTiedot.asiakasAvain, laskuAvain, historiaAvain)
    batch.set(historiaUri, toistuvaLasku)
  }

  async poistaToistuvaLasku(toistuvaLasku: ToistuvaLasku): Promise<void> {
    const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()

    const toistuvaLaskuUri = this.laskuUriService.getToistuvanLaskunUri(kayttajanTiedot.asiakasAvain, toistuvaLasku.lasku.avain)
    const batch = this.lemonaidFirebase.firestoreBatch()

    batch.update(toistuvaLaskuUri, {
      poistettu: true,
      seuraavaLahetysPvm: null,
      paivitetty: this.timestampService.now(),
      paivittaja: kayttajanTiedot.uid,
    })

    const toistuvaHistoria: ToistuvaLasku = {
      lasku: toistuvaLasku.lasku,
      toisto: toistuvaLasku.toisto,
      seuraavaLahetysPvm: null,
      lahetyksia: toistuvaLasku.lahetyksia,
      poistettu: true,
      paivitetty: this.timestampService.now(),
      paivittaja: kayttajanTiedot.uid,
    }
    this.lisaaToistuvanLaskunHistoriaMerkinta(kayttajanTiedot, toistuvaLasku.lasku.avain, toistuvaHistoria, batch)

    await batch.commit()
  }

  asetaToistuvanLaskunEmailTiedot(juurilasku: Lasku, kasiteltava: LaskuBase): void {
    juurilasku.email = {
      start: null,
      done: null,
      vastaanottajat: kasiteltava.email?.vastaanottajat || [],
      aihe: kasiteltava.email?.aihe || '',
      otsikko: kasiteltava.email?.otsikko || '',
      teksti: kasiteltava.email?.teksti || '',
      slogan: kasiteltava.email?.slogan || '',
      template: kasiteltava.email?.template || null,
      footer: 'footer2',
    }
    delete juurilasku.sahkoinen
    delete juurilasku.print
  }

  asetaToistuvanLaskunSahkoinenTiedot(juurilasku: Lasku, osoite: LaskunSahkoinenOsoite): void {
    juurilasku.sahkoinen = {
      start: null,
      done: null,
      osoite: osoite,
      status: null,
    }
    delete juurilasku.email
    delete juurilasku.print
  }

  asetaToistuvanLaskunPrintTiedot(juurilasku: Lasku): void {
    juurilasku.print = {
      start: null,
      done: null,
    }
    delete juurilasku.email
    delete juurilasku.sahkoinen
  }

  async merkitseLaskuLahetetyksiTulostamalla(juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'laheta-itse', juurilasku, kasiteltavaAvain: kasiteltava.avain }, kasiteltava)
  }

  async merkitseLaskunMaksumuistutusLahetetyksiTulostamalla(juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'muistutus-itse', juurilasku, kasiteltavaAvain: kasiteltava.avain }, kasiteltava)
  }

  async merkitseLaskunMaksumuistutusLahetetyksiSpostilla(
    templateId: number,
    juurilasku: Lasku,
    kasiteltava: LaskuBase,
    vastaanottajat: EmailLahetysStatus[],
    aihe: string,
    otsikko: string,
    teksti: string,
    slogan: string,
    paivitaAsiakkaanSaajat: boolean,
  ): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku(
      {
        toiminto: 'muistutus-sposti',
        juurilasku,
        kasiteltavaAvain: kasiteltava.avain,
        sposti: { templateId, vastaanottajat, aihe, otsikko, teksti, slogan },
        paivitaLahettajatAsiakkaaseen: paivitaAsiakkaanSaajat,
      },
      kasiteltava,
    )
  }

  async merkitseLaskunPerintaLahetetyksiSpostilla(juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    return this._tallennaLasku({ toiminto: 'perinta-sposti', juurilasku, kasiteltavaAvain: kasiteltava.avain }, kasiteltava)
  }

  async merkitseLaskunPerintaPerutuksiSpostilla(juurilasku: Lasku): Promise<JuurilaskuJaKasiteltava> {
    return this._tallennaLasku({ toiminto: 'perinta-peruutus', juurilasku })
  }

  async merkitseLaskuLahetetyksiSpostilla(
    templateId: number,
    juurilasku: Lasku,
    kasiteltava: LaskuBase,
    vastaanottajat: EmailLahetysStatus[],
    aihe: string,
    otsikko: string,
    teksti: string,
    slogan: string,
  ): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku(
      {
        toiminto: 'laheta-sposti',
        juurilasku,
        kasiteltavaAvain: kasiteltava.avain,
        sposti: { templateId, vastaanottajat, aihe, otsikko, teksti, slogan },
      },
      kasiteltava,
    )
  }

  async merkitseLaskuLahetetyksiSahkoisesti(osoite: LaskunSahkoinenOsoite, juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'laheta-sahkoinen', juurilasku, kasiteltavaAvain: kasiteltava.avain, sahkoinenOsoite: osoite }, kasiteltava)
  }

  async merkitseLaskunMaksumuistutusLahetetyksiSahkoisesti(osoite: LaskunSahkoinenOsoite, juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'muistutus-sahkoinen', juurilasku, kasiteltavaAvain: kasiteltava.avain, sahkoinenOsoite: osoite }, kasiteltava)
  }

  async lahetaUudelleenSpostilla(
    templateId: number,
    juurilasku: Lasku,
    kasiteltava: LaskuBase,
    vastaanottajat: EmailLahetysStatus[],
    aihe: string,
    otsikko: string,
    teksti: string,
    slogan: string,
  ): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku(
      {
        toiminto: 'uudelleen-sposti',
        juurilasku,
        kasiteltavaAvain: kasiteltava.avain,
        sposti: { templateId, vastaanottajat, aihe, otsikko, teksti, slogan },
      },
      kasiteltava,
    )
  }

  async lahetaUudelleenSahkoisesti(osoite: LaskunSahkoinenOsoite, juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'uudelleen-sahkoinen', juurilasku, kasiteltavaAvain: kasiteltava.avain, sahkoinenOsoite: osoite }, kasiteltava)
  }

  async lahetaUudelleenTulostamalla(juurilasku: Lasku, kasiteltava: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    this.valmisteleTallennustaVarten(kasiteltava)
    return this._tallennaLasku({ toiminto: 'uudelleen-itse', juurilasku, kasiteltavaAvain: kasiteltava.avain }, kasiteltava)
  }

  private async _tallennaLasku(pyynto: WebLaskuTallennaPyynto, kasiteltava?: LaskuBase): Promise<JuurilaskuJaKasiteltava> {
    const lahetettavaJuurilasku = this._annaPalvelimelleLahetettavaJuurilasku(pyynto.juurilasku, kasiteltava)
    const pyyntoJuurilaskulla: WebLaskuTallennaPyynto = { ...pyynto, juurilasku: lahetettavaJuurilasku }
    const lahetettavaPyynto = this._kopioijaPalvelu.kopioiIlmanAsettamattomiaKenttia(pyyntoJuurilaskulla)
    const vastaus = await this.lemonaidFirebase.functionsCall<WebLaskuTallennaPyynto, WebLaskuTallennaVastaus>('laskuTallennaWeb', lahetettavaPyynto, undefined, 'europe-north1')
    if (!vastaus || vastaus.e) {
      throw new Error(`Laskun tallennus palvelimella epäonnistui (toiminto: ${pyynto.toiminto}, virhe: ${vastaus?.e})`)
    }
    const tallennettuJuurilasku = this.timestampService.fixTimestamps(vastaus.juurilasku) as Lasku
    const tallennettuKasiteltava = this.timestampService.fixTimestamps(vastaus.kasiteltava) as LaskuBase
    this.laskuKopioija.siirraLaskuTiedot(tallennettuJuurilasku, pyynto.juurilasku)
    const kasiteltavaltaPuuttuiAvain = !!kasiteltava && (!kasiteltava.avain || kasiteltava.avain === UUDEN_LASKUN_AVAIN)
    if (kasiteltavaltaPuuttuiAvain) {
      kasiteltava.avain = tallennettuKasiteltava.avain
    }
    return { juurilasku: tallennettuJuurilasku, kasiteltava: tallennettuKasiteltava }
  }

  private _annaPalvelimelleLahetettavaJuurilasku(juurilasku: Lasku, kasiteltava?: LaskuBase): Lasku {
    const lahetettavaJuurilasku: Lasku = { ...juurilasku }
    if (juurilasku.liitteet) {
      lahetettavaJuurilasku.liitteet = this._annaLiitteetIlmanLataustietoja(juurilasku.liitteet)
    }
    let liittyvatLaskut = juurilasku.liittyvatLaskut
    const onkoKasiteltavaLiittyvaLasku = !!kasiteltava && kasiteltava.avain !== juurilasku.avain
    if (onkoKasiteltavaLiittyvaLasku) {
      liittyvatLaskut = { ...juurilasku.liittyvatLaskut, [kasiteltava.avain]: kasiteltava }
    }
    if (liittyvatLaskut) {
      lahetettavaJuurilasku.liittyvatLaskut = {}
      for (const [avain, liittyvaLasku] of Object.entries(liittyvatLaskut)) {
        lahetettavaJuurilasku.liittyvatLaskut[avain] = this._annaLaskuIlmanLiitteidenLataustietoja(liittyvaLasku)
      }
    }
    return lahetettavaJuurilasku
  }

  private _annaLaskuIlmanLiitteidenLataustietoja(lasku: LaskuBase): LaskuBase {
    const lahetettavaLasku: LaskuBase = { ...lasku }
    if (lasku.liitteet) {
      lahetettavaLasku.liitteet = this._annaLiitteetIlmanLataustietoja(lasku.liitteet)
    }
    return lahetettavaLasku
  }

  private _annaLiitteetIlmanLataustietoja(liitteet: LaskunLiitetiedosto[]): LaskunLiitetiedosto[] {
    return liitteet.map(liite => {
      const tallennettavaLiite: LadattavaLiitetiedosto = { ...liite }
      delete tallennettavaLiite.uploadData
      delete tallennettavaLiite.uploadDone
      delete tallennettavaLiite.uploadDonePercentage
      delete tallennettavaLiite.uploadError
      return tallennettavaLiite
    })
  }

  async getPdfUrl(lasku: LaskuBase): Promise<string> {
    const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
    const url = this.laskuUriService.getPdfUri(kayttajanTiedot.asiakasId, lasku.avain)
    return url
  }

  annaPdfTiedostonNimi(juurilasku: Lasku, kasiteltava: LaskuBase, kieli: TuettuKieli): string {
    let nimi = this.translationService.lokalisoi('lasku.tiedostonimen-alku-ladattaessa', kieli)
    const laskunumero = this.shared.annaMuotoiltuLaskunumero(juurilasku, kasiteltava)
    if (laskunumero) {
      nimi += ' ' + laskunumero
    }
    if (kasiteltava && kasiteltava.asiakas && kasiteltava.asiakas.nimi) {
      nimi += ' ' + kasiteltava.asiakas.nimi
    }
    if (kasiteltava && kasiteltava.pvm) {
      nimi += ' ' + this.dateService.muotoilePaiva(kasiteltava.pvm, kieli)
    }
    nimi += '.pdf'

    return nimi
  }

  annaProformaPdfTiedostonNimi(kasiteltava: LaskuBase, kieli: TuettuKieli): string {
    let nimi = this.translationService.lokalisoi('lasku.tiedostonimen-alku-proforma', kieli)
    if (kasiteltava?.asiakas?.nimi) {
      nimi += ` ${kasiteltava.asiakas.nimi}`
    }
    if (kasiteltava?.pvm) {
      nimi += ` ${this.dateService.muotoilePaiva(kasiteltava.pvm, kieli)}`
    }
    return `${nimi}.pdf`
  }

  annaPdfTiedostonNimiVersiolle(juurilasku: Lasku, kasiteltava: LaskuBase, kieli: TuettuKieli): string {
    let nimi = this.translationService.lokalisoi('lasku.tiedostonimen-alku-ladattaessa', kieli)
    const laskunumero = this.shared.annaMuotoiltuLaskunumero(juurilasku, kasiteltava)
    if (laskunumero) {
      nimi += ' ' + laskunumero
    }
    if (kasiteltava && kasiteltava.asiakas && kasiteltava.asiakas.nimi) {
      nimi += ' ' + kasiteltava.asiakas.nimi
    }
    if (kasiteltava && kasiteltava.pvm) {
      nimi += ' ' + this.dateService.muotoilePaiva(kasiteltava.pvm, kieli)
    }
    if (kasiteltava && kasiteltava.date) {
      const asDate = this.dateService.timestampToDate(kasiteltava.date)
      nimi += ', tallennettu ' + this.dateService.muotoilePaivaJaAikaDate(asDate, kieli)
    }
    nimi += '.pdf'

    return nimi
  }

  validoiPeppolOsoite(osoite: string, kieli: TuettuKieli): Promise<PeppolValidaatioTulos> {
    if (!osoite) {
      return Promise.resolve({ error: false, message: this.translationService.lokalisoi('lasku.peppol-validaatio.osoite-eivalidi', kieli) })
    }

    const address: PeppolNumberRequest = { osoite: this.stringService.removeAllWhiteSpaces(osoite) }
    return this.lemonaidFirebase
      .functionsCall<PeppolNumberRequest, PeppolNumberResponse>('validatePeppolAddress', address)
      .then(response => {
        if (response && response.matches && response.matches.length > 0) {
          const entities = response.matches.flatMap(match => match.entities)
          if (entities.length === 1) {
            const entity = entities[0]
            const name = this.getPeppolEntityName(entity)
            return { error: false, message: this.translationService.lokalisoi('lasku.peppol-validaatio.osoite-kuuluu', kieli, { nimi: name, maa: entity.countryCode }) }
          } else {
            const multipleResults = entities
              .map(entity => {
                const name = this.getPeppolEntityName(entity)
                return `${name} ${entity.countryCode}`
              })
              .join(', ')
            return { error: false, message: this.translationService.lokalisoi('lasku.peppol-validaatio.loytyi-yritykset', kieli, { yritykset: multipleResults }) }
          }
        } else {
          return { error: true, message: this.translationService.lokalisoi('lasku.peppol-validaatio.osoite-eivalidi', kieli) }
        }
      })
      .catch(error => {
        console.error(error)
        return { error: true, message: this.translationService.lokalisoi('lasku.peppol-validaatio.osoite-eivalidi', kieli) }
      })
  }

  private getPeppolEntityName(entity: PeppolNumberResponseEntity): string {
    if (Array.isArray(entity.name)) {
      const arr = entity.name as Array<PeppolNameObject>
      if (arr.length > 0) {
        return arr[0].name
      }
    } else if (this.stringService.isString(entity.name)) {
      return entity.name as string
    }

    return ''
  }
}
